import { Brand, OverrideFieldType, ProductFullAttribute } from 'API';
import Dropdown from 'components/common/Dropdown/Dropdown';
import Input from 'components/common/Input/Input';
import Label from 'components/common/Label';
import NumberInput from 'components/common/NumberInput/NumberInput';
import { getDefaultLabOriginId } from 'configurations/DefaultLabOrigin';
import { useDaysInLab } from 'hooks/use-days-in-lab';
import { map } from 'lodash';
import { OrderModuleActionsContext, OrderModuleContext } from 'providers/OrderModuleProvider';
import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { DropdownModel, LocalOrderItemInput, LocalOrderProductAttributeInput } from 'shared/models';
import { OverrideField } from './types';

interface IProps {
  productDepartmentAttribute: ProductFullAttribute;
  orderItem: LocalOrderItemInput;
  productBrands: Brand[];
  isOrderUpdate?: boolean;
  salesCategoryAndBackLogGroupAttributeRecord: Record<string, string[]>;
  salesCategoryAndBackLogGroupProductRecord: Record<string, string[]>;
}

/**
 * Production Facility:
 * Defaults to the production facility associated whth the lab that the billing account id belongs to.
 * On edit, should load the production facility that was set during case creation.
 * Department:
 * Defaults to the department attribute default value coming from Product Admin (if available).
 * On edit, should load the department set during case creation.
 * Days In Lab:
 * Is set to the days in lab assigned to the product for the specific manufacturing location (production facility).
 * Days in lab is updated when Add-Ons and/or Services are added that have days in lab set in Product Admin.
 * On edit, should load the values that were set during case creation.
 * @param productBrands - The list of product brands.
 * @param productDepartmentAttribute - The product department attribute.
 * @param orderItem - The order item.
 * @param isOrderUpdate - Indicates whether the component is used for updating an existing order. (optional)
 * @returns JSX element representing the ProductionFacility component.
 */
const ProductionFacility: React.FC<IProps> = ({
  productBrands,
  productDepartmentAttribute,
  orderItem,
  isOrderUpdate,
  salesCategoryAndBackLogGroupAttributeRecord,
  salesCategoryAndBackLogGroupProductRecord,
}) => {
  const { updateOrderItem } = useContext(OrderModuleActionsContext);
  const { order, onSubmitAlertCount, isDigitalOrder } = useContext(OrderModuleContext);
  const manufacturingLocation = orderItem.manufacturingLocation;
  const selectedDaysInLab = orderItem.daysInLab;
  const orderItemId = orderItem.id;
  const productCode = orderItem.productCode;
  const addOnString = JSON.stringify(orderItem.addOns);
  const attributesString = JSON.stringify(orderItem.attributes);
  const daysInLabValueChangedRef = useRef(false);

  const productBrandsResult = useMemo(() => {
    return {
      [orderItem.productCode]: productBrands,
    };
  }, [orderItem.productCode, productBrands]);

  const { getProductLevelDaysInLab, calculateTotalDaysInLab, calculateMaxDaysInLab } =
    useDaysInLab(productBrandsResult);

  const selectedProductionFacility = useMemo(() => {
    return {
      primaryLabel: manufacturingLocation,
      value: manufacturingLocation,
    };
  }, [manufacturingLocation]);

  //setting attribute defaultValue value to department field(readonly)
  const selectedDepartmentValue = useMemo<string>(() => {
    if (productDepartmentAttribute.defaultValue) {
      return productDepartmentAttribute.defaultValue;
    } else {
      return '';
    }
  }, [productDepartmentAttribute.defaultValue]);

  const getDaysInLabForAddedAttributes = useCallback(
    (productionFacility: string) => {
      const daysInLabProductionFacility = getProductLevelDaysInLab({
        productionFacility,
        isDigitalOrder,
        productCode,
      });
      /**
       * NOTE: using json stringify and parse so that we know when the array is updated.
       * Otherwise, if a property is updated in one of the objects in the array, this function won't re-render.
       */
      const addOns = JSON.parse(addOnString) as LocalOrderProductAttributeInput[];
      const attributes = JSON.parse(attributesString) as LocalOrderProductAttributeInput[];

      // calculate total days in lab for add-ons/services
      const totalAddonDaysInLab = calculateTotalDaysInLab({
        attributeNames: map(addOns, 'name'),
        productionFacility,
        isDigitalOrder,
        productCode,
      });

      // get a values of salesCategoryAndBackLogGroupAttributeRecord and flatten the array
      const salesCategoryAndBackLogGroupAttributeNames = Object.values(
        salesCategoryAndBackLogGroupAttributeRecord
      ).flat();

      // check if sales category and backlog group attributes exist
      const checkIfSalesCategoryAndBackLogExist = attributes.some(attribute => {
        return salesCategoryAndBackLogGroupAttributeNames.includes(attribute.name);
      });

      // check if sales category and backlog group at product level days in lab exist
      const isProductLevelDaysInLabExist = Object.values(salesCategoryAndBackLogGroupProductRecord)
        .flat()
        .includes(productCode);

      // if isProductLevelDaysInLabExist is true and sales category and backlog group attributes do not exist, return product level days in lab + add-on days in lab
      if (isProductLevelDaysInLabExist && !checkIfSalesCategoryAndBackLogExist) {
        return daysInLabProductionFacility + (totalAddonDaysInLab || 0);
      }

      /**
       * As per the Quenton's comment in https://glidewell.atlassian.net/browse/LMS1-5933?focusedCommentId=75980,
       * We need to remove the duplicate attributes from the list to avoid double counting days in lab.
       */
      const nonDuplicateAttributes = attributes.filter((attribute, index) => {
        // If the attribute is empty, we don't want to count it in the days in lab.
        if (!attribute.value) return false;
        const currentIndex = attributes.findIndex(a => a.name === attribute.name && a.type === attribute.type);
        return currentIndex === index;
      });

      let totalMaxDaysInLab = 0;
      // calculate the max days in lab for sales category and backlog group attributes
      Object.values(salesCategoryAndBackLogGroupAttributeRecord).forEach(attributeNames => {
        const attributes = nonDuplicateAttributes.filter(a => attributeNames.includes(a.name));
        // calculate the max days in lab for each attribute
        // and sum them up to get the total max days in lab
        totalMaxDaysInLab +=
          calculateMaxDaysInLab({
            attributeNames: map(attributes, 'name'),
            productionFacility,
            isDigitalOrder,
            productCode,
          }) || 0;
      });
      const filteredNonSalesCategoryAndBackLogGroupAttributes = nonDuplicateAttributes.filter(attribute => {
        return !salesCategoryAndBackLogGroupAttributeNames.includes(attribute.name);
      });

      /**
       * calculate the total days in lab for non-sales category and backlog group attributes
       * if totalAttributesDaysInLab is null, use the days in lab for the production facility, or else use the totalAttributesDaysInLab
       */
      const totalAttributesDaysInLab = calculateTotalDaysInLab({
        attributeNames: map(filteredNonSalesCategoryAndBackLogGroupAttributes, 'name'),
        productionFacility,
        isDigitalOrder,
        productCode,
        returnNullIfExist: true,
      });

      // If no days in lab is found, defaults to the value for days in lab for production facility.
      if (totalMaxDaysInLab === null && totalAttributesDaysInLab === null && totalAddonDaysInLab === null)
        return daysInLabProductionFacility;
      // sum of max days in lab for sales category and backlog group attributes, total days in lab for non-sales category and backlog group attributes, and add-on days in lab
      return (totalMaxDaysInLab || 0) + (totalAttributesDaysInLab || 0) + (totalAddonDaysInLab || 0);
    },
    [
      getProductLevelDaysInLab,
      isDigitalOrder,
      productCode,
      addOnString,
      attributesString,
      calculateTotalDaysInLab,
      salesCategoryAndBackLogGroupAttributeRecord,
      salesCategoryAndBackLogGroupProductRecord,
      calculateMaxDaysInLab,
    ]
  );

  const calculatedDaysInLab = useMemo(
    () => getDaysInLabForAddedAttributes(selectedProductionFacility.value),
    [selectedProductionFacility.value, getDaysInLabForAddedAttributes]
  );

  const updateProductionFacility = useCallback(
    (productionFacility: string) => {
      const prodFacilityLowerCase = productionFacility.toLowerCase();
      const locationData = productBrands.find(data => data.brandName.toLowerCase() === prodFacilityLowerCase);
      const calculatedDaysInLab = getDaysInLabForAddedAttributes(productionFacility);
      updateOrderItem(orderItemId, {
        manufacturingLocation: productionFacility,
        manufacturingLocationId: locationData?.siteId ? +locationData.siteId : 0,
        daysInLab: calculatedDaysInLab,
      });
    },
    [productBrands, getDaysInLabForAddedAttributes, updateOrderItem, orderItemId]
  );

  /**
   * Returns the default site id.
   * Defaults to the brand associated with the default lab origin id if there is no originFacilityId set on the order yet.
   */
  const defaultProductBrand = useMemo(() => {
    const defaultBrand = productBrands.find(p => p.siteId === getDefaultLabOriginId()) || productBrands[0];

    // originFacilityId gets set on the order when a billing account id is searched.
    if (!order.originFacilityId) {
      return defaultBrand;
    }

    return productBrands.find(p => p.siteId === order.originFacilityId.toString()) || defaultBrand;
  }, [order.originFacilityId, productBrands]);

  useEffect(() => {
    if (order.originFacilityId && !selectedProductionFacility.value) {
      if (isOrderUpdate && orderItem?.manufacturingLocation) return; // if it order update and manufacturingLocation is already present ie, it is not a newly added orderItem, Then don't set default production facility

      // Update order with production facility
      const defaultProductionFacility = defaultProductBrand?.brandName || '';
      updateProductionFacility(defaultProductionFacility);
    }
  }, [
    defaultProductBrand,
    isOrderUpdate,
    order.originFacilityId,
    orderItem?.manufacturingLocation,
    selectedProductionFacility.value,
    updateProductionFacility,
  ]);

  /**
   * Checks if the new selected value for days in lab overrides the default value.
   * If it does, adds it to the overrides array for this order.
   * @param newDaysInLab - The new value selected for days in lab.
   */
  const handleOverrideCheck = useCallback(
    (fieldName: string, defaultValue: string, newValue: string) => {
      // Filters the overwritten field to avoid duplicates. It will be re-evaluated in the next if statement.
      let orderOverrides =
        orderItem.overrides?.filter(override => {
          // If the field is production facility, also clears days in lab, since the UI will reset its value.
          const isProductionFacility = fieldName === OverrideField.ProductionFacility;
          return isProductionFacility
            ? override.field !== OverrideField.ProductionFacility && override.field !== OverrideField.DaysInLab
            : override.field !== fieldName;
        }) || [];
      // When changing the production facility, resets the days in lab override for this product, since that value would have reset.
      if (fieldName === OverrideField.ProductionFacility) {
        orderOverrides = orderOverrides.filter(orderOverride => orderOverride.field !== OverrideField.DaysInLab);
      }
      // If the value for days in lab does not match the default, adds it to the list of overwritten attributes.
      if (defaultValue && newValue !== defaultValue) {
        orderOverrides.push({
          new: newValue,
          old: defaultValue,
          type: OverrideFieldType.Default,
          field: fieldName,
        });
      }

      // Adds the updates overrides to the target order item.
      updateOrderItem(orderItemId, { overrides: orderOverrides });
    },
    [orderItem.overrides, orderItemId, updateOrderItem]
  );

  useEffect(() => {
    if (daysInLabValueChangedRef.current) {
      daysInLabValueChangedRef.current = false;
      return;
    }

    const isDaysInLabChanged = calculatedDaysInLab !== selectedDaysInLab;
    if (isDaysInLabChanged) {
      updateOrderItem(orderItemId, { daysInLab: calculatedDaysInLab });
      handleOverrideCheck(OverrideField.DaysInLab, String(calculatedDaysInLab), String(calculatedDaysInLab));
    }
  }, [
    calculatedDaysInLab,
    isOrderUpdate,
    orderItemId,
    selectedDaysInLab,
    updateOrderItem,
    attributesString,
    addOnString,
    handleOverrideCheck,
  ]);

  /**
   * Handles production facility select.
   * @param e - The new production facility value.
   */
  const handleProductionFacilitySelect = (e: DropdownModel) => {
    updateProductionFacility(e.value);
    // Update order with production facility
    const defaultProductionFacility = defaultProductBrand?.brandName || '';
    handleOverrideCheck(OverrideField.ProductionFacility, defaultProductionFacility, e.value);
  };

  /**
   * Handles days in lab select.
   * @param newDaysInLab - The new days in lab value.
   */
  const handleDaysInLabSelect = (newDaysInLab: string) => {
    updateOrderItem(orderItemId, { daysInLab: +newDaysInLab });
    handleOverrideCheck(OverrideField.DaysInLab, String(calculatedDaysInLab), newDaysInLab);
    daysInLabValueChangedRef.current = true;
  };

  return (
    <div className="grid grid-cols-6 gap-6">
      <div className="col-span-3">
        <Dropdown
          label="Production Facility"
          isRequired
          selected={selectedProductionFacility}
          data={productBrands.map(p => ({ primaryLabel: p.brandName, value: p.brandName }))}
          setSelected={handleProductionFacilitySelect}
          onSubmitAlert={!!onSubmitAlertCount}
        />
      </div>
      <div className="col-span-2">
        <Input
          id="department"
          label="Department"
          name="department"
          type="text"
          value={selectedDepartmentValue}
          isRequired
          readOnly
        />
      </div>
      <div className="col-span-1">
        <Label required>Days in Lab</Label>
        <NumberInput
          id={`daysInLabInput`}
          value={selectedDaysInLab}
          min={1}
          onChange={handleDaysInLabSelect}
          onSubmitAlert={!!onSubmitAlertCount}
          isRequired
        />
      </div>
    </div>
  );
};

export default ProductionFacility;
